/**
 * The Bonnaroo Arch — procedural Three.js geometry.
 *
 * Two variants:
 *  - Classic (default): rainbow mirror/sequin half-circle.
 *  - Modern: a square LED frame with emissive panels.
 *
 * Per SPEC.md §1, only the classic Arch ships in v1, but the toggle exists
 * so we can flip in v2 without rebuilding the model.
 */

import * as THREE from 'three';

export interface ArchOptions {
  /** Outer radius of the half-circle in meters. */
  radius?: number;
  /** Thickness of the arch ribbon in meters. */
  thickness?: number;
  /** Depth of the ribbon (front-to-back) in meters. */
  depth?: number;
  /** Use the classic rainbow mirror Arch. */
  classic?: boolean;
}

const RAINBOW = [
  '#ff0040',
  '#ff7a1a',
  '#ffd400',
  '#3ad600',
  '#00b3ff',
  '#7c3aff',
  '#ff3aa3'
];

export class Arch {
  static build(opts: ArchOptions = {}): THREE.Group {
    const radius = opts.radius ?? 22;
    const thickness = opts.thickness ?? 2.4;
    const depth = opts.depth ?? 4.0;
    const classic = opts.classic ?? true;

    const group = new THREE.Group();
    group.name = classic ? 'Arch.Classic' : 'Arch.Modern';

    if (classic) {
      // Build a half-torus by extruding a thick strip along a half-circle Shape.
      // Stack 7 concentric rings to get the rainbow band effect.
      const ringCount = RAINBOW.length;
      const ringWidth = thickness / ringCount;

      for (let i = 0; i < ringCount; i++) {
        const r = radius - i * ringWidth;
        const shape = new THREE.Shape();
        shape.absarc(0, 0, r, 0, Math.PI, false);
        shape.lineTo(-r + ringWidth, 0);
        shape.absarc(0, 0, r - ringWidth, Math.PI, 0, true);
        shape.lineTo(r, 0);

        const geo = new THREE.ExtrudeGeometry(shape, {
          depth,
          bevelEnabled: true,
          bevelSize: 0.05,
          bevelThickness: 0.05,
          bevelSegments: 2,
          curveSegments: 48
        });
        geo.translate(0, 0, -depth / 2);

        const mat = new THREE.MeshPhysicalMaterial({
          color: RAINBOW[i],
          metalness: 0.85,
          roughness: 0.18,
          clearcoat: 1.0,
          clearcoatRoughness: 0.1,
          envMapIntensity: 1.2
        });

        const ring = new THREE.Mesh(geo, mat);
        ring.castShadow = true;
        ring.receiveShadow = true;
        group.add(ring);
      }

      // Two cylindrical footings under each leg of the arch.
      const footGeo = new THREE.CylinderGeometry(thickness * 0.7, thickness * 0.9, 2, 16);
      const footMat = new THREE.MeshStandardMaterial({
        color: '#2a2030',
        roughness: 0.6,
        metalness: 0.3
      });
      for (const sign of [-1, 1]) {
        const foot = new THREE.Mesh(footGeo, footMat);
        foot.position.set(sign * (radius - thickness / 2), 1, 0);
        group.add(foot);
      }
    } else {
      // Modern square LED frame.
      const frameThickness = thickness;
      const w = radius * 1.6;
      const h = radius * 1.4;
      const frameGeo = new THREE.BoxGeometry(w, frameThickness, depth);
      const sideGeo = new THREE.BoxGeometry(frameThickness, h, depth);
      const matFrame = new THREE.MeshStandardMaterial({
        color: '#101015',
        roughness: 0.4,
        metalness: 0.7
      });
      const matLed = new THREE.MeshBasicMaterial({ color: '#7c3aff' });

      const top = new THREE.Mesh(frameGeo, matFrame);
      top.position.y = h;
      group.add(top);

      const left = new THREE.Mesh(sideGeo, matFrame);
      left.position.set(-w / 2, h / 2, 0);
      group.add(left);

      const right = new THREE.Mesh(sideGeo, matFrame);
      right.position.set(w / 2, h / 2, 0);
      group.add(right);

      // Inner LED panel as an emissive plane.
      const panelGeo = new THREE.PlaneGeometry(w - frameThickness * 2, h - frameThickness);
      const panel = new THREE.Mesh(panelGeo, matLed);
      panel.position.set(0, h / 2, depth / 2 + 0.01);
      group.add(panel);
    }

    return group;
  }
}
